/**
 * Host layout for the hybrid tenancy model.
 *
 * `app.<baseDomain>` is the global login host: it has no workspace, so no tenant session can exist
 * there unless the URL names one at `/t/{slug}`. Dedicated workspaces still run on their own host
 * (`clinic1.<baseDomain>` or a stored tenant URL).
 */
const normalizeDomain = (value: string | undefined): string | null => {
  const trimmed = (value || '').trim().toLowerCase().replace(/^\.+|\.+$/g, '');
  return trimmed.length > 0 ? trimmed : null;
};

const BASE_DOMAIN = normalizeDomain(process.env.REACT_APP_PUBLIC_BASE_DOMAIN);
const APP_HOST_LABEL = (process.env.REACT_APP_APP_HOST_LABEL || 'app').trim().toLowerCase() || 'app';
const DEV_GLOBAL_AUTH = String(process.env.REACT_APP_DEV_GLOBAL_AUTH || '').trim().toLowerCase() === 'true';

export const PATH_TENANT_PREFIX = 't';

const PATH_SLUG_PATTERN = /^\/t\/([a-z0-9](?:[a-z0-9-]{0,48}[a-z0-9])?)(?:\/|$)/i;

const currentHostname = (): string =>
  (typeof window === 'undefined' ? '' : window.location.hostname).trim().toLowerCase();

const currentPathname = (): string =>
  typeof window === 'undefined' ? '' : window.location.pathname;

export const getBaseDomain = (): string | null => BASE_DOMAIN;

export const getAppHost = (): string | null => (BASE_DOMAIN ? `${APP_HOST_LABEL}.${BASE_DOMAIN}` : null);

export const getAppHostUrl = (): string | null => {
  const host = getAppHost();
  return host ? `https://${host}` : null;
};

export const buildWorkspaceUrl = (slug: string): string | null => {
  const normalized = slug.trim().toLowerCase();
  return BASE_DOMAIN && normalized ? `https://${normalized}.${BASE_DOMAIN}` : null;
};

export const getPathTenantSlug = (pathname?: string): string | null => {
  const match = (pathname ?? currentPathname()).match(PATH_SLUG_PATTERN);
  if (!match) {
    return null;
  }

  const slug = match[1].toLowerCase();
  return slug.length >= 2 ? slug : null;
};

export const getRouterBasename = (): string => {
  const slug = getPathTenantSlug();
  return slug ? `/${PATH_TENANT_PREFIX}/${slug}` : '';
};

export const isPathTenantHost = (): boolean => getPathTenantSlug() !== null;

export const buildPathWorkspaceUrl = (slug: string): string | null => {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  const appHostUrl = getAppHostUrl();
  if (appHostUrl) {
    return `${appHostUrl}/${PATH_TENANT_PREFIX}/${normalized}`;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  return `${window.location.origin}/${PATH_TENANT_PREFIX}/${normalized}`;
};

export const pathTenantHeaders = (): Record<string, string> => {
  const slug = getPathTenantSlug();
  return slug ? { 'X-Tenant-Slug': slug } : {};
};

export const applyPathTenantHeaders = (headers: Headers): void => {
  const slug = getPathTenantSlug();
  if (slug && !headers.has('X-Tenant-Slug')) {
    headers.set('X-Tenant-Slug', slug);
  }
};

/**
 * The deployed global host, with no `/t/{slug}` workspace selected. Nothing here can hold a
 * session, so the app must not try to restore one.
 */
export const isTenantlessAuthHost = (): boolean => {
  if (isPathTenantHost()) {
    return false;
  }

  const appHost = getAppHost();
  return appHost !== null && currentHostname() === appHost;
};

/**
 * Local development stands in for the app host without needing DNS: the picker runs on localhost and
 * the handoff code is redeemed on that same origin instead of a workspace hostname.
 */
export const isDevGlobalAuthEnabled = (): boolean =>
  DEV_GLOBAL_AUTH && !isTenantlessAuthHost() && !isPathTenantHost();

export const isGlobalAuthHost = (): boolean => isTenantlessAuthHost() || isDevGlobalAuthEnabled();
